/**
 * AuthCallback — handles the return from Emergent-managed Google OAuth.
 *
 * Flow (per Emergent Auth Integration Playbook):
 *   1. User clicks "Continue with Google" on /login or /signup
 *   2. Browser navigates to https://auth.emergentagent.com/?redirect=<origin>/app
 *   3. After Google auth, Emergent returns to <origin>/app#session_id=XYZ
 *   4. App.js detects the `#session_id=` fragment SYNCHRONOUSLY and renders
 *      this component (NOT in a useEffect — that would race the
 *      AuthProvider's initial /auth/me check)
 *   5. This component POSTs the session_id to /api/auth/google/session,
 *      receives the standard { token, user } envelope, stores both in
 *      localStorage exactly as login() does, and navigates to /app
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, readErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from '@/lib/toast';
import { gtmPush } from '@/lib/gtm';
import { clearRegionCache } from '@/lib/region';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [error, setError] = useState(null);
    // useRef (NOT useState) — per playbook, prevents StrictMode double-fire.
    const hasProcessed = useRef(false);

    useEffect(() => {
        // Synchronous guard at the top — handles React StrictMode's
        // double-invocation of effects in dev.
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const run = async () => {
            // Extract session_id from URL fragment (NOT query string).
            const hash = window.location.hash || '';
            const match = hash.match(/session_id=([^&]+)/);
            const sessionId = match ? decodeURIComponent(match[1]) : '';
            if (!sessionId) {
                setError('Missing Google session. Please try signing in again.');
                return;
            }

            // Strip the fragment immediately — session_id is single-use,
            // and we don't want it lingering in the address bar.
            try {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
            } catch { /* ignore */ }

            // Pick up referral code (set on landing by ReferralCapture).
            const referralCode = (localStorage.getItem('ac_ref') || '').trim() || undefined;

            try {
                const { data } = await api.post('/auth/google/session', {
                    session_id: sessionId,
                    referral_code: referralCode,
                });
                // Store JWT + user the same way email/password login does
                // so the existing AuthProvider + api interceptor work
                // unchanged across the rest of the app.
                localStorage.setItem('ac_token', data.token);
                localStorage.setItem('ac_user', JSON.stringify(data.user));
                // Round 40n — drop the anon-IP region cached during
                // the pre-login landing visit so the post-Google-OAuth
                // user picks up their persisted residence_country.
                clearRegionCache();
                setUser(data.user);

                // Best-effort timezone capture (A31 — same as email/password login).
                try {
                    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    if (tz) await api.put('/auth/me', { display_timezone: tz });
                } catch { /* ignore */ }

                // GTM signup event — only on first Google sign-in for this user.
                if (data.signup_event) {
                    gtmPush({
                        event:    'signup',
                        user_id:  data.user?.id,
                        method:   'google',
                    });
                }

                toast.success(`Welcome${data.user?.name ? ', ' + data.user.name.split(' ')[0] : ''}!`);
                // Send the user straight into the app. If they're a brand
                // new Google signup without birth_details, Protected
                // will redirect them to /onboarding automatically.
                navigate('/app', { replace: true });
            } catch (err) {
                setError(readErr(err, 'Could not complete Google sign-in. Please try again.'));
            }
        };
        run();
    }, [navigate, setUser]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-obsidian px-6" data-testid="auth-callback-page">
            {!error ? (
                <div className="flex flex-col items-center gap-4 text-parchment">
                    <Loader2 className="animate-spin text-marigold" size={32} />
                    <div className="font-serif text-xl">Signing you in...</div>
                    <div className="text-sm text-slate-400">Confirming your Google account</div>
                </div>
            ) : (
                <div className="max-w-md text-center" data-testid="auth-callback-error">
                    <div className="font-serif text-2xl text-parchment mb-3">Sign-in could not complete</div>
                    <div className="text-sm text-slate-400 mb-6">{error}</div>
                    <button
                        onClick={() => navigate('/login', { replace: true })}
                        className="bg-marigold/20 hover:bg-marigold/30 text-marigold border border-marigold/40 rounded-sm px-5 py-2 text-sm"
                        data-testid="auth-callback-back-btn"
                    >
                        Back to login
                    </button>
                </div>
            )}
        </div>
    );
}
